import axios from "axios";
import { Constants } from "../constants/Variables";
import { CreateErrorNotification } from "../components/common/Notifications/Notification";

/**
 * Fetches the list of all available sections and their variables
 * Data Manipulation: 
 * - Extracts section list from API response
 * - Determines default selected section from first key
 * @returns {Object} Contains sectionList and defaultSelectedSection
 */
// export const fetchSectionsList = async () => {
//     try {
//       const res = await axios.get(
//         process.env.REACT_APP_SECTIONS_AND_VARIABLES_ENDPOINT
//       );
//       let sectionList = res.data.output.data;
//       const defaultSelectedSection = Object.keys(Object.keys(sectionList)[0])
//       return {
//         "sectionList": sectionList,
//         "defaultSelectedSection": defaultSelectedSection
//       };
//     } catch (error) {
//       CreateErrorNotification(error);
//     }
// };

export const fetchSectionsList = async () => {
  try {
    const response = await axios({
      method: 'get',
      url: process.env.REACT_APP_SECTIONS_AND_VARIABLES_ENDPOINT
    });
    let sectionList = response.data.output.data;
    const defaultSelectedSection = Object.keys(sectionList)[0]
    return {
      "sectionList": sectionList,
      "defaultSelectedSection": defaultSelectedSection
    }
  } catch (error) {
    CreateErrorNotification(error);
  }
}

/**
 * Retrieves filters associated with a specific section/variable ID
 * Data Handling:
 * - Converts ID to JSON string for API request
 * - Returns raw filter data from response
 * @param {string} id - The section/variable identifier
 * @returns {Object} Filter data for the specified ID
 */
export const fetchFiltersList = async (id) => {
    try {
        var data = JSON.stringify({
            "id": id
        });
        var config = {
            method: 'post',
            url: process.env.REACT_APP_FILTERS_ENDPOINT,
            headers: { 
              'Content-Type': 'application/json'
            },
            data : data
          };
          
          const res = await axios(config)
        return res.data.data;
    } catch (error) {
      CreateErrorNotification(error);
    }
};

/**
 * Fetches source information for a specific variable at a given year
 * Data Transformation:
 * - Formats year to required date string (YYYY-01-01)
 * - Returns first item from source data array
 * @param {string} source - Source identifier
 * @param {number|string} year - Year for which source is needed
 * @returns {Object} Source information
 */
export const fetchSource = async (source, year) => {
  try {
    year = year.toString() + "-01-01"
    var data = JSON.stringify({
        "source": source,
        "date": year
    });
    var config = {
        method: 'post',
        url: process.env.REACT_APP_VARIABLE_SOURCE_ENDPOINT,
        headers: { 
          'Content-Type': 'application/json'
        },
        data : data
      };
      
      const res = await axios(config)
    return res.data.output.data[0];
  } catch (error) {
    CreateErrorNotification(error);
  }
};

/**
 * Retrieves variable data based on visualization type and parameters
 * Data Processing:
 * - Handles different data formats for Line vs other visualizations
 * - For Line charts: Fetches all-year data
 * - For other charts: Fetches single-year data
 * - Supports facet iteration for first fetch
 * 
 * @param {string} visualization - Type of visualization (Line/Bar/Map)
 * @param {string} id - Variable identifier
 * @param {Object} facet - Facet information
 * @param {number|string} year - Selected year
 * @param {Array} allFacets - All available facets
 * @param {string} areaType - Geographic area type
 * @param {boolean} firstFetchFlag - Indicates initial data fetch
 * @returns {Object} Processed variable data with facet information
 */
export const fetchVariableData = async (visualization, id, facet, year, allFacets, areaType, firstFetchFlag) => {
  const fetchData = async (visualization, id, facet, year) => {
    try {
      var data, config;
      if(visualization===Constants.Line){
        data = JSON.stringify({
          "id": id,
          "vid": facet.vid,
          "sector": areaType
        });
        config = {
          method: 'post',
          url: process.env.REACT_APP_VARIABLE_DATA_ALL_YEAR_ENDPOINT,
          headers: { 
            'Content-Type': 'application/json'
          },
          data : data
        };
          
        const res = await axios(config)
        return res.data.output;
      }
      else{
        year = year.toString() + "-01-01"
        data = JSON.stringify({
          "id": id,
          "vid": facet.vid,
          "date": year,
          "sector": areaType
        });
        config = {
          method: 'post',
          url: process.env.REACT_APP_VARIABLE_DATA_SINGLE_YEAR_ENDPOINT,
          headers: { 
            'Content-Type': 'application/json'
          },
          data : data
        };
          
        const res = await axios(config)
        return res.data.output;
      }
    } catch (error) {
      CreateErrorNotification(error);
    }
  }
  let resp;
  if(firstFetchFlag){
    let i;
    for(i=0;i<allFacets.length;i++){
      resp = await fetchData(visualization, id, allFacets[i], year);
      if(resp?.data.length > 0){
        resp["facet"] = allFacets[i];
        return resp;
      }
    }
  }
  else{
    resp = await fetchData(visualization, id, facet, year);
    resp["facet"] = facet;
  }
  return resp;
};

/**
 * Retrieves notes/annotations associated with a specific facet
 * Data Handling:
 * - Uses facet's vid (variable ID) to fetch related notes
 * - Returns raw note data from API
 * @param {Object} facet - Facet object containing vid
 * @returns {Object} Notes data for the specified facet
 */
export const fetchFacetNote = async (facet) => {
  try {
    var data = JSON.stringify({
      "vid": facet.vid,
    });
    var config = {
      method: 'post',
      url: process.env.REACT_APP_FACET_NOTES_API,
      headers: { 
        'Content-Type': 'application/json'
      },
      data : data
    };
      
    const res = await axios(config)
    return res.data.output;
  } catch (error) {
    CreateErrorNotification(error);
  }
}